// src/components/Dashboard/CreditReports.js

import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { Panel } from "primereact/panel";
import { useEffect, useRef, useState } from "react";
import Styles from "../../styles/_global/Styles";
import LiquidGlass from "../LiquidGlass";

function createData(key, date, score, download_uuid) {
  return { key, date, score, download_uuid };
}

export default function CreditReports() {
  const fetchingRef = useRef(false);
  const [creditReports, setCreditReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    getCreditReports();
  }, []);

  const getCreditReports = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/credit-report/all`,
        {
          uuid: JSON.parse(localStorage.getItem("user")).uuid,
        },
        {
          headers: { "x-api-key": process.env.REACT_APP_BACKEND_API_KEY },
        }
      );
      const reports = res.data.data.map((report) =>
        createData(
          report.uuid,
          new Date(report.createdAt).toLocaleDateString(),
          report.metadata.crScore.find((s) => s.score_Order === 1)?.score,
          report.uuid
        )
      );
      setCreditReports(reports);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (uuid) => {
    setIsDownloading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/credit-report/download`,
        { uuid },
        {
          headers: { "x-api-key": process.env.REACT_APP_BACKEND_API_KEY },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `creditReport-${uuid}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  // only glass-style on mobile alerts
  const alertGlass = isMobile
    ? {
        backgroundColor: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#fff",
        textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
        "& .MuiAlert-icon": {
          color: "#fff",
        },
      }
    : {};

  return (
    <Box>
      <LiquidGlass
        altComponent={Panel}
        title="Report History"
        icon="pi pi-calendar-clock"
        sx={{ padding: 1 }}
        altProps={{
          header: (
            <>
              <i className="pi pi-calendar-clock" /> Report History
            </>
          ),
        }}
      >
        {isLoading ? (
          <Skeleton variant="rounded" width="100%" height={200} />
        ) : (
          <>
            <Alert severity="info" sx={alertGlass}>
              This is a list of all the credit reports you've requested through{" "}
              {process.env.REACT_APP_NAME}. You can download them by clicking
              the "Download" button.
            </Alert>
            <TableContainer sx={{ mt: 2 }}>
              <Table aria-label="report history table">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: isMobile ? "#fff" : "inherit",
                        textShadow: isMobile
                          ? "0px 1px 2px rgba(0,0,0,0.5)"
                          : "none",
                      }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: isMobile ? "#fff" : "inherit",
                        textShadow: isMobile
                          ? "0px 1px 2px rgba(0,0,0,0.5)"
                          : "none",
                      }}
                    >
                      Score
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: isMobile ? "#fff" : "inherit",
                        textShadow: isMobile
                          ? "0px 1px 2px rgba(0,0,0,0.5)"
                          : "none",
                      }}
                    >
                      Download
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {creditReports.map((report) => (
                    <TableRow
                      key={report.key}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell
                        sx={{
                          color: isMobile ? "#fff" : "inherit",
                          textShadow: isMobile
                            ? "0px 1px 2px rgba(0,0,0,0.5)"
                            : "none",
                        }}
                      >
                        {report.date}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: isMobile ? "#fff" : "inherit",
                          textShadow: isMobile
                            ? "0px 1px 2px rgba(0,0,0,0.5)"
                            : "none",
                        }}
                      >
                        {report.score}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          onClick={() => handleDownload(report.download_uuid)}
                          sx={{
                            backgroundColor: Styles.primary.main,
                            "&:hover": {
                              backgroundColor: Styles.secondary.main,
                            },
                            textTransform: "none",
                            color: "#fff",
                          }}
                          size="small"
                          disabled={isDownloading}
                        >
                          {isDownloading ? (
                            <CircularProgress size={24} />
                          ) : (
                            <DownloadIcon />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </LiquidGlass>
    </Box>
  );
}
